import { useEffect, useRef, useState, type ReactNode } from 'react';
import { api } from '../api';
import {
  ApiError,
  type RankingEvento,
  type RankingLinha,
  type RankingMinhas,
  type RankingPeriodo,
  type RankingRegras,
  type RankingTabela,
  type RankingTipo
} from '../api/types';
import { idiomaAtual, numero, t } from '../i18n/idioma';
import { useIdioma } from '../i18n/useIdioma';
import { useFechar } from '../input/useAtalhos';

/*
  Rankings (docs/techspec-rankings.md, protótipo em docs/prototipos/rankings-nota.md).
  Mesma casca da Configuração (.modal > .p-cfg > .gump, navegação à esquerda); no celular vira
  tela cheia com as seções em abas, pelas regras de hoje. Tudo do shard do personagem.
*/

type Aba = RankingTipo | 'minhas' | 'regras';
const ABAS: readonly Aba[] = ['individual', 'guildas', 'pk', 'ub', 'duelo', 'minhas', 'regras'];

interface Props {
  onClose(): void;
  abaInicial?: Aba;
}

const SVG: Record<string, ReactNode> = {
  individual: <><path d="M14.5 3.5 20.5 9.5" /><path d="M4 20l5-1 10-10-4-4L5 15l-1 5z" /></>,
  guildas: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 10h6M12 7v9" /></>,
  pk: <><circle cx="12" cy="10" r="6" /><path d="M9 9h.01M15 9h.01M10 13h4M9 16v4M15 16v4M12 16v4" /></>,
  ub: <><path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0z" /><path d="M7 6H4v1a3 3 0 0 0 3 3M17 6h3v1a3 3 0 0 1-3 3" /></>,
  duelo: <><path d="M14.5 3.5 20.5 9.5M9.5 3.5 3.5 9.5" /><path d="M4 20l5-1 6-6M20 20l-5-1-6-6" /></>,
  minhas: <><path d="M4 5h16M4 12h16M4 19h10" /><path d="M17 17l2 2 3-4" /></>,
  regras: <><path d="M6 3h9l4 4v14H6z" /><path d="M14 3v5h5M9 12h7M9 16h7" /></>,
  temp: <><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></>,
  busca: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  kill: <><path d="M14.5 3.5 20.5 9.5" /><path d="M4 20l5-1 10-10-4-4L5 15l-1 5z" /></>,
  morte: <><path d="M12 3a7 7 0 0 0-4 12.7V19h8v-3.3A7 7 0 0 0 12 3z" /><path d="M9 11h.01M15 11h.01M10 19v2M14 19v2" /></>,
  relogio: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
  par: <path d="M7 7h11l-3-3M17 17H6l3 3" />,
  novato: <><circle cx="12" cy="8" r="4" /><path d="M6 21v-2a6 6 0 0 1 12 0v2" /></>,
  rede: <path d="M5 12a10 10 0 0 1 14 0M8.5 15.5a5 5 0 0 1 7 0M12 19h.01" />,
  aliado: <><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /><path d="M9 12l2 2 4-4" /></>,
  credito: <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />,
  olho: <><path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  premio: <><path d="M20 12v9H4v-9M2 7h20v5H2zM12 21V7" /><path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" /></>
};

function Ic({ k }: { k: string }) {
  return <svg viewBox="0 0 24 24" aria-hidden="true">{SVG[k]}</svg>;
}

const NOTO = ['inocente', 'criminoso', 'assassino'] as const;

function Nick({ l, voce }: { l: { nome: string; tag?: string | null; noto: number }; voce?: boolean }) {
  return (
    <>
      <span className={`p-rk-n p-rk-n--${NOTO[l.noto] ?? 'inocente'}`}>{l.nome}</span>
      {l.tag ? <span className="p-rk-tag">[{l.tag}]</span> : null}
      {voce ? <span className="p-rk-voce">{t('rankings.voce')}</span> : null}
    </>
  );
}

function Pos({ n }: { n: number }) {
  if (n <= 0) return <>—</>;
  return n <= 3 ? <span className={`p-rk-med p-rk-med--${n}`}>{n}</span> : <>{n}</>;
}

function kd(k: number, m: number): string {
  return (k / Math.max(1, m)).toLocaleString(idiomaAtual(), { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function hora(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString(idiomaAtual(), { hour: '2-digit', minute: '2-digit' });
}

function data(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(idiomaAtual(), { day: '2-digit', month: '2-digit' });
}

function quando(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const hoje = new Date();
  const ontem = new Date(Date.now() - 86_400_000);
  if (d.toDateString() === hoje.toDateString()) return t('rankings.hoje', { hora: hora(iso) });
  if (d.toDateString() === ontem.toDateString()) return t('rankings.ontem', { hora: hora(iso) });
  return `${data(iso)} ${hora(iso)}`;
}

function faltamDias(fim: string): number {
  return Math.max(0, Math.ceil((new Date(fim).getTime() - Date.now()) / 86_400_000));
}

export function RankingsModal({ onClose, abaInicial = 'individual' }: Props) {
  useIdioma();
  useFechar(onClose, { modal: true });

  const [aba, setAba] = useState<Aba>(abaInicial);
  const [periodo, setPeriodo] = useState<RankingPeriodo>('temporada');
  const [ordem, setOrdem] = useState<'kills' | 'kd'>('kills');
  const [busca, setBusca] = useState('');
  const [buscaFeita, setBuscaFeita] = useState('');
  const [tabela, setTabela] = useState<RankingTabela | null>(null);
  const [minhas, setMinhas] = useState<RankingMinhas | null>(null);
  const [regras, setRegras] = useState<RankingRegras | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [aberto, setAberto] = useState<string | null>(null);
  const [evAberto, setEvAberto] = useState<number | null>(null);
  const [filtroEv, setFiltroEv] = useState<'tudo' | 'ok' | 'nao'>('tudo');
  const pedido = useRef(0);
  const navRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const id = window.setTimeout(() => setBuscaFeita(busca), 300);
    return () => window.clearTimeout(id);
  }, [busca]);

  useEffect(() => {
    api.rankingRegras().then(setRegras).catch(() => { /* as regras aparecem quando a aba abrir */ });
  }, []);

  useEffect(() => {
    const n = ++pedido.current;
    setErro(null);
    setAberto(null);
    if (aba === 'regras') {
      if (regras) return;
      setCarregando(true);
      api.rankingRegras().then(r => { if (n === pedido.current) setRegras(r); })
        .catch(e => { if (n === pedido.current) setErro(e instanceof ApiError ? e.message : t('rankings.falha')); })
        .finally(() => { if (n === pedido.current) setCarregando(false); });
      return;
    }
    setCarregando(true);
    const p = aba === 'minhas'
      ? api.rankingMinhas().then(m => { if (n === pedido.current) setMinhas(m); })
      : api.rankingTabela(aba, periodo, ordem, buscaFeita).then(tb => { if (n === pedido.current) setTabela(tb); });
    p.catch(e => { if (n === pedido.current) setErro(e instanceof ApiError ? e.message : t('rankings.falha')); })
      .finally(() => { if (n === pedido.current) setCarregando(false); });
  }, [aba, periodo, ordem, buscaFeita, regras]);

  // No celular as abas rolam de lado: a escolhida fica à vista.
  useEffect(() => {
    const nav = navRef.current;
    const sel = nav?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (nav && sel && nav.scrollWidth > nav.clientWidth) nav.scrollLeft = sel.offsetLeft - nav.clientWidth / 2 + sel.offsetWidth / 2;
  }, [aba]);

  const trocar = (a: Aba) => {
    setAba(a);
    setBusca('');
    setBuscaFeita('');
  };

  const desc: Record<Aba, string> = {
    individual: t('rankings.desc.individual'),
    guildas: t('rankings.desc.guildas'),
    pk: t('rankings.desc.pk'),
    ub: t('rankings.desc.ub'),
    duelo: t('rankings.desc.duelo'),
    minhas: minhas ? t('rankings.desc.minhasN', { ok: minhas.killsOk, nao: minhas.killsNao }) : t('rankings.desc.minhas'),
    regras: t('rankings.desc.regras')
  };

  let painel: ReactNode;
  if (erro) painel = <p className="notice" role="alert">{erro}</p>;
  else if (aba === 'regras') painel = regras ? <Regras r={regras} /> : <p className="p-rk-vazio">{t('comum.carregando')}</p>;
  else if (aba === 'minhas') painel = minhas
    ? <Minhas m={minhas} filtro={filtroEv} onFiltro={setFiltroEv} aberto={evAberto} onAbrir={setEvAberto} onRegras={() => trocar('regras')} />
    : <p className="p-rk-vazio">{t('comum.carregando')}</p>;
  else painel = (
    <>
      {tabela && <Temporada tb={tabela} />}
      <div className="p-rk__barra">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <div className="p-seg" role="radiogroup" aria-label={t('rankings.periodo')}>
            {(['semana', 'temporada', 'geral'] as const).map(p => (
              <button type="button" role="radio" key={p} aria-checked={periodo === p} onClick={() => setPeriodo(p)}>{t(`rankings.periodo.${p}`)}</button>
            ))}
          </div>
          {aba === 'individual' && (
            <div className="p-seg" role="radiogroup" aria-label={t('rankings.ordem')}>
              <button type="button" role="radio" aria-checked={ordem === 'kills'} onClick={() => setOrdem('kills')}>{t('rankings.col.kills')}</button>
              <button type="button" role="radio" aria-checked={ordem === 'kd'} onClick={() => setOrdem('kd')}>K/D</button>
            </div>
          )}
        </div>
        <label className="p-rk__busca">
          <Ic k="busca" />
          <input value={busca} maxLength={32} onChange={e => setBusca(e.target.value)}
            placeholder={aba === 'guildas' ? t('rankings.buscarGuilda') : t('rankings.buscarPersonagem')} aria-label={t('rankings.buscar')} />
        </label>
      </div>
      {tabela && tabela.periodo === 'temporada' && tabela.premios.length > 0 && <FaixaPremios tb={tabela} />}
      {tabela && tabela.tipo === aba
        ? <Tabela tb={tabela} aberto={aberto} onAbrir={id => setAberto(a => a === id ? null : id)} />
        : <p className="p-rk-vazio">{t('comum.carregando')}</p>}
      {tabela && tabela.tipo === aba && <Nota tb={tabela} onRegras={() => trocar('regras')} />}
    </>
  );

  return (
    <div className="modal" role="presentation" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={t('rankings.janela')} className="p-cfg p-rk" onClick={e => e.stopPropagation()}>
        <section className="gump">
          <header className="gump__head">
            <h2 className="gump__title">{t('rankings.janela')}</h2>
            <button type="button" className="btn btn--tiny btn--ghost win__close" onClick={onClose} aria-label={t('comum.fechar')}>×</button>
          </header>
          <div className="gump__body p-cfg__corpo">
            <nav className="p-cfg__nav" role="tablist" aria-label={t('rankings.janela')} ref={navRef}>
              {ABAS.map(id => (
                <button type="button" role="tab" key={id} aria-selected={aba === id} onClick={() => trocar(id)}>
                  <Ic k={id} />
                  <span><b>{t(`rankings.aba.${id}`)}</b><small>{desc[id]}</small></span>
                </button>
              ))}
            </nav>
            <div className="p-cfg__painel" role="tabpanel" aria-busy={carregando}>
              {painel}
            </div>
          </div>
          <footer className="p-cfg__pe">
            <span>{tabela && aba !== 'minhas' && aba !== 'regras' ? t('rankings.atualizado', { hora: hora(tabela.atualizadoEm) }) : ''}</span>
            <button type="button" className="btn btn--tiny win__close" onClick={onClose}>{t('comum.fechar')}</button>
          </footer>
        </section>
      </div>
    </div>
  );
}

function Temporada({ tb }: { tb: RankingTabela }) {
  return (
    <div className="p-rk__temp">
      <Ic k="temp" />
      <span>
        <b>{t('rankings.temporada', { n: tb.temporada.numero })}</b>
        <small>{t('rankings.temporadaDatas', { inicio: data(tb.temporada.inicio), fim: data(tb.temporada.fim) })}</small>
      </span>
      <time>{t('rankings.faltam', { n: faltamDias(tb.temporada.fim) })}</time>
    </div>
  );
}

function FaixaPremios({ tb }: { tb: RankingTabela }) {
  return (
    <p className="p-rk__premio">
      <b>{t('rankings.premios')}</b>
      {tb.premios.map(p => (
        <span key={`${p.de}-${p.ate}`}><em>{p.de === p.ate ? `${p.de}º` : `${p.de}º–${p.ate}º`}</em>{p.qtd > 1 ? `${numero(p.qtd)} ` : ''}{p.nome}</span>
      ))}
      {tb.tipo === 'guildas' && <span className="p-rk__premio-dica">{t('rankings.premioLider')}</span>}
    </p>
  );
}

function Tabela({ tb, aberto, onAbrir }: { tb: RankingTabela; aberto: string | null; onAbrir(id: string): void }) {
  const elo = tb.tipo === 'ub' || tb.tipo === 'duelo';
  const fixo = tb.voce && !tb.linhas.some(l => l.id === tb.voce!.id) ? tb.voce : null;
  const colunas = elo ? 7 : tb.tipo === 'guildas' ? 6 : tb.tipo === 'pk' ? 6 : 5;

  const cab = elo ? (
    <tr><th>#</th><th>{t('rankings.col.personagem')}</th><th aria-sort="descending">Elo</th><th className="esconde">{t('rankings.col.ultima')}</th><th>{t('rankings.col.v')}</th><th>{t('rankings.col.d')}</th><th className="esconde">{t('rankings.col.aprov')}</th></tr>
  ) : tb.tipo === 'guildas' ? (
    <tr><th>#</th><th>{t('rankings.col.guilda')}</th><th aria-sort="descending">{t('rankings.col.kills')}</th><th>{t('rankings.col.mortes')}</th><th>K/D</th><th className="esconde">{t('rankings.col.guerras')}</th></tr>
  ) : tb.tipo === 'pk' ? (
    <tr><th>#</th><th>{t('rankings.col.assassino')}</th><th aria-sort="descending">{t('rankings.col.kills')}</th><th>{t('rankings.col.inocentes')}</th><th>{t('rankings.col.mortes')}</th><th className="esconde">K/D</th></tr>
  ) : (
    <tr><th>#</th><th>{t('rankings.col.personagem')}</th><th aria-sort={tb.ordem === 'kills' ? 'descending' : undefined}>{t('rankings.col.kills')}</th><th>{t('rankings.col.mortes')}</th><th aria-sort={tb.ordem === 'kd' ? 'descending' : undefined}>K/D</th></tr>
  );

  const linha = (l: RankingLinha, pe = false) => {
    const ab = !pe && aberto === l.id;
    const k = l.kills ?? 0;
    const m = l.mortes ?? 0;
    const cls = `${l.voce ? 'is-voce' : ''}${ab ? ' is-aberto' : ''}`;
    let celulas: ReactNode;
    if (elo) {
      const partidas = (l.vitorias ?? 0) + (l.derrotas ?? 0) + (l.empates ?? 0);
      celulas = l.provisorio ? (
        <td className="nome" colSpan={6}>
          <Nick l={l} voce={l.voce} />
          <span className="p-rk-sub">{t('rankings.provisorio', { elo: numero(l.elo ?? tb.regras.eloInicial), v: l.vitorias ?? 0, d: l.derrotas ?? 0, falta: Math.max(0, tb.regras.minimo - partidas) })}</span>
        </td>
      ) : (
        <>
          <td className="nome"><Nick l={l} voce={l.voce} /></td>
          <td className="kd">{l.elo != null ? numero(l.elo) : '—'}</td>
          <td className="esconde">{l.delta != null ? <span className={l.delta >= 0 ? 'p-rk-delta--mais' : 'p-rk-delta--menos'}>{l.delta >= 0 ? '+' : '−'}{Math.abs(l.delta)}</span> : '—'}</td>
          <td>{l.vitorias ?? 0}</td>
          <td>{l.derrotas ?? 0}</td>
          <td className="esconde">{partidas ? `${Math.round(((l.vitorias ?? 0) / partidas) * 100)}%` : '—'}</td>
        </>
      );
    } else if (tb.tipo === 'guildas') {
      celulas = (
        <>
          <td className="nome">
            <b className="p-rk-gn">{l.nome}</b><span className="p-rk-tag">[{l.tag}]</span>
            {l.voce ? <span className="p-rk-voce">{t('rankings.suaGuilda')}</span> : null}
            <span className="p-rk-sub">{t('rankings.pontuaram', { n: l.pontuaram ?? 0, total: l.membros ?? 0 })}</span>
          </td>
          <td className="kd">{numero(k)}</td><td>{numero(m)}</td>
          <td>{k >= tb.regras.guildaKdMin ? kd(k, m) : <span className="fraco">—</span>}</td>
          <td className="esconde">{l.guerrasV ?? 0}–{l.guerrasD ?? 0}</td>
        </>
      );
    } else if (tb.tipo === 'pk') {
      celulas = (
        <>
          <td className="nome"><Nick l={l} voce={l.voce} /><span className="p-rk-sub">{t('rankings.contagens', { n: l.contagens ?? 0 })}</span></td>
          <td className="kd">{numero(k)}</td><td>{numero(l.inocentes ?? 0)}</td><td>{numero(m)}</td><td className="esconde">{kd(k, m)}</td>
        </>
      );
    } else {
      celulas = (
        <>
          <td className="nome"><Nick l={l} voce={l.voce} /></td>
          <td className={tb.ordem === 'kills' ? 'kd' : ''}>{numero(k)}</td>
          <td>{numero(m)}</td>
          <td className={tb.ordem === 'kd' ? 'kd' : ''}>{k >= tb.regras.kdMin ? kd(k, m) : <span className="fraco" title={t('rankings.kdMin', { n: tb.regras.kdMin })}>—</span>}</td>
        </>
      );
    }
    return (
      <>
        <tr className={cls} onClick={pe ? undefined : () => onAbrir(l.id)}>
          <td className="pos"><Pos n={l.pos} /></td>
          {celulas}
        </tr>
        {ab && (
          <tr className="p-rk-det"><td colSpan={colunas}><div className="p-rk-det__in"><Detalhe l={l} tipo={tb.tipo} /></div></td></tr>
        )}
      </>
    );
  };

  return (
    <table className="p-rk-t">
      <thead>{cab}</thead>
      <tbody>
        {tb.linhas.length === 0
          ? <tr><td colSpan={colunas} className="p-rk-vazio">{t('rankings.vazio')}</td></tr>
          : tb.linhas.map(l => <Linha key={l.id}>{linha(l)}</Linha>)}
      </tbody>
      {fixo && <tfoot>{linha(fixo, true)}</tfoot>}
      {!fixo && !tb.voce && tb.tipo === 'pk' && (
        <tfoot><tr className="is-voce"><td className="pos">—</td><td className="nome" colSpan={5}><span className="p-rk-sub">{t('rankings.pkSoAssassino')}</span></td></tr></tfoot>
      )}
    </table>
  );
}

function Linha({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

function Detalhe({ l, tipo }: { l: RankingLinha; tipo: RankingTipo }) {
  if (tipo === 'guildas') return (
    <>
      <div><b>{t('rankings.det.guerras')}</b>{t('rankings.det.guerrasValor', { v: l.guerrasV ?? 0, d: l.guerrasD ?? 0 })}</div>
      <div><b>{t('rankings.det.conta')}</b>{t('rankings.det.contaGuilda')}</div>
    </>
  );
  if (tipo === 'ub' || tipo === 'duelo') return (
    <>
      <div><b>{t('rankings.det.ultima')}</b>{(l.delta ?? 0) >= 0 ? '+' : '−'}{Math.abs(l.delta ?? 0)} Elo</div>
      <div><b>{t('rankings.det.sequencia')}</b>{t('rankings.det.vitorias', { n: l.sequencia ?? 0 })}</div>
      <div><b>{t('rankings.det.empates')}</b>{l.empates ?? 0}</div>
    </>
  );
  const k = l.kills ?? 0;
  return (
    <>
      {tipo === 'individual' && <div><b>{t('rankings.det.onde')}</b>{t('rankings.det.ondeValor', { mundo: numero(k - (l.killsGuerra ?? 0)), guerra: numero(l.killsGuerra ?? 0) })}</div>}
      <div><b>{t('rankings.det.ultimaKill')}</b>{l.ultima ? quando(l.ultima) : '—'}</div>
      <div><b>K/D</b>{t('rankings.det.kd', { k: numero(k), m: numero(Math.max(1, l.mortes ?? 0)) })}</div>
    </>
  );
}

function Nota({ tb, onRegras }: { tb: RankingTabela; onRegras(): void }) {
  const chave = tb.tipo === 'individual' ? 'rankings.nota.individual'
    : tb.tipo === 'guildas' ? 'rankings.nota.guildas'
    : tb.tipo === 'pk' ? 'rankings.nota.pk'
    : 'rankings.nota.elo';
  return (
    <p className="p-rk__nota">
      {t(chave, { kdMin: tb.regras.kdMin, guildaKdMin: tb.regras.guildaKdMin, minimo: tb.regras.minimo, elo: numero(tb.regras.eloInicial) })}{' '}
      <button type="button" className="p-rk__link" onClick={onRegras}>{t('rankings.comoConta')}</button>
    </p>
  );
}

const ICONE_MOTIVO: Record<string, string> = { protecao: 'relogio', par: 'par', iniciante: 'novato', aliado: 'aliado', verif: 'rede', conta: 'aliado', equipe: 'olho', anulada: 'olho', erro: 'olho' };

function Minhas({ m, filtro, onFiltro, aberto, onAbrir, onRegras }: {
  m: RankingMinhas;
  filtro: 'tudo' | 'ok' | 'nao';
  onFiltro(f: 'tudo' | 'ok' | 'nao'): void;
  aberto: number | null;
  onAbrir(id: number | null): void;
  onRegras(): void;
}) {
  const lista = m.eventos.filter(e => filtro === 'tudo' || (filtro === 'ok' ? e.resultado === 'ok' : e.resultado !== 'ok'));
  return (
    <>
      <div className="p-rk-num">
        <div><b>{numero(m.killsOk)}</b><small>{t('rankings.minhas.killsOk')}</small></div>
        <div className="is-nao"><b>{numero(m.killsNao)}</b><small>{t('rankings.minhas.killsNao')}</small></div>
        <div><b>{numero(m.mortesOk)}</b><small>{t('rankings.minhas.mortesOk')}</small></div>
        <div className="is-nao"><b>{numero(m.mortesNao)}</b><small>{t('rankings.minhas.mortesNao')}</small></div>
      </div>
      <div className="p-rk__barra">
        <p className="p-rk__nota" style={{ maxWidth: '34em' }}>{t('rankings.minhas.explica')}</p>
        <div className="p-seg" role="radiogroup" aria-label={t('rankings.minhas.mostrar')}>
          {(['tudo', 'ok', 'nao'] as const).map(f => (
            <button type="button" role="radio" key={f} aria-checked={filtro === f} onClick={() => onFiltro(f)}>{t(`rankings.minhas.filtro.${f}`)}</button>
          ))}
        </div>
      </div>
      <div className="p-rk-ev">
        {lista.length === 0 && <p className="p-rk-vazio">{t('rankings.minhas.vazio')}</p>}
        {lista.map(e => <Evento key={e.id} e={e} aberto={aberto === e.id} onAbrir={() => onAbrir(aberto === e.id ? null : e.id)} onRegras={onRegras} protecaoMin={m.protecaoMin} />)}
      </div>
    </>
  );
}

function Evento({ e, aberto, onAbrir, onRegras, protecaoMin }: { e: RankingEvento; aberto: boolean; onAbrir(): void; onRegras(): void; protecaoMin: number }) {
  const outro = { nome: e.outro, tag: e.tag, noto: e.noto };
  const rot = e.resultado === 'ok' ? ['ok', t('rankings.res.ok')] : e.resultado === 'verif' ? ['verif', t('rankings.res.verif')] : ['nao', t('rankings.res.nao')];
  const motivo = e.tipo === 'morte' && e.motivo === 'protecao' ? 'protecaoMorte' : e.motivo;
  return (
    <div className={`p-rk-ev__l is-${e.tipo}`}>
      <button type="button" aria-expanded={aberto} onClick={onAbrir}>
        <Ic k={e.tipo} />
        <span>
          <b>{e.tipo === 'kill' ? <>{t('rankings.minhas.voceMatou')} <Nick l={outro} /></> : <><Nick l={outro} /> {t('rankings.minhas.matouVoce')}</>}</b>
          <small>{e.contexto === 'guerra' ? `${t('rankings.minhas.guerra')} · ` : ''}{quando(e.quando)}</small>
        </span>
        <span className={`p-tag p-tag--${rot[0]}`}>{rot[1]}</span>
      </button>
      {aberto && (
        <div className="p-rk-ev__por">
          {e.resultado === 'ok' ? (
            <>
              <p>{t(e.tipo === 'kill' ? 'rankings.minhas.contouKill' : 'rankings.minhas.contouMorte')}</p>
              <p className="regra">{t(e.tipo === 'kill' ? 'rankings.minhas.creditoVoce' : 'rankings.minhas.creditoOutro', { nome: e.outro })}</p>
            </>
          ) : (
            <>
              <p className="p-rk-ev__mot"><Ic k={ICONE_MOTIVO[motivo] ?? 'olho'} />
                <span><b>{t(`rankings.motivo.${motivo || 'equipe'}`)}.</b> {t(`rankings.motivo.${motivo || 'equipe'}.texto`, {
                  nome: e.outro, min: protecaoMin, hora: hora(e.voltaAs)
                })}</span></p>
              <p className="regra"><button type="button" className="p-rk__link" onClick={onRegras}>{t('rankings.verRegras')}</button></p>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function Regras({ r }: { r: RankingRegras }) {
  const R = ({ k, h, children }: { k: string; h: string; children: ReactNode }) => (
    <div className="p-rk-regra"><Ic k={k} /><div><h4>{h}</h4>{children}</div></div>
  );
  const v = (x: string | number) => <span className="v">{x}</span>;
  return (
    <>
      <p className="p-rk__nota">{t('rankings.regras.topo', { versao: r.versao })}</p>
      <div className="p-rk-regras">
        <R k="credito" h={t('rankings.regras.credito')}><p>{t('rankings.regras.creditoTexto')}</p></R>
        <R k="relogio" h={t('rankings.regras.protecao')}>
          <p>{t('rankings.regras.protecaoTexto1')} {v(t('rankings.min', { n: r.protecaoMin }))} {t('rankings.regras.protecaoTexto2')}</p>
          <p>{t('rankings.regras.revanche')}</p>
        </R>
        <R k="par" h={t('rankings.regras.par')}><p>{t('rankings.regras.parTexto1')} {v(t('rankings.regras.parValor', { n: r.parTeto }))}{t('rankings.regras.parTexto2')}</p></R>
        <R k="novato" h={t('rankings.regras.iniciante')}><p>{t('rankings.regras.inicianteTexto1')} {v(t('rankings.horas', { n: r.iniciantesH }))} {t('rankings.regras.inicianteTexto2')}</p></R>
        <R k="rede" h={t('rankings.regras.rede')}><p>{t('rankings.regras.redeTexto')}</p></R>
        <R k="aliado" h={t('rankings.aba.guildas')}><p>{t('rankings.regras.guildasTexto1')} {v(t('rankings.horas', { n: r.guildaCarenciaH }))}{t('rankings.regras.guildasTexto2')}</p></R>
        <R k="pk" h={t('rankings.aba.pk')}><p>{t('rankings.regras.pkTexto')}</p></R>
        <R k="ub" h={t('rankings.regras.elo')}>
          <p>{t('rankings.regras.eloTexto1')} {v(numero(r.eloInicial))}{t('rankings.regras.eloTexto2')}</p>
          <p>{t('rankings.regras.eloUb', { minimo: r.ubMinimo, dia: r.ubParDia, s: r.ubAbandonoS })}</p>
          <p>{t('rankings.regras.eloDuelo', { minimo: r.dueloMinimo, dia: r.dueloParDia, s: r.dueloAbandonoS })}</p>
        </R>
        <R k="temp" h={t('rankings.regras.onde')}>
          <table className="p-rk-mapa">
            <thead><tr><th /><th>{t('rankings.aba.individual')}</th><th>{t('rankings.aba.guildas')}</th><th>PK</th><th>UB</th><th>{t('rankings.aba.duelo')}</th></tr></thead>
            <tbody>
              <tr><td>{t('rankings.regras.mundo')}</td><td className="s">✓</td><td className="s">✓</td><td className="s">{t('rankings.regras.seAssassino')}</td><td className="n">—</td><td className="n">—</td></tr>
              <tr><td>{t('rankings.regras.guerra')}</td><td className="s">✓</td><td className="s">✓</td><td className="s">{t('rankings.regras.seAssassino')}</td><td className="n">—</td><td className="n">—</td></tr>
              <tr><td>{t('rankings.aba.duelo')}</td><td className="n">—</td><td className="n">—</td><td className="n">—</td><td className="n">—</td><td className="s">Elo</td></tr>
              <tr><td>Ultimate Battle</td><td className="n">—</td><td className="n">—</td><td className="n">—</td><td className="s">Elo</td><td className="n">—</td></tr>
            </tbody>
          </table>
          <p style={{ marginTop: 6 }}>{t('rankings.regras.temporadas', { dias: r.temporadaDias })}</p>
        </R>
        <R k="premio" h={t('rankings.premios')}><p>{t('rankings.regras.premiosTexto')}</p></R>
        <R k="olho" h={t('rankings.regras.denuncie')}><p>{t('rankings.regras.denuncieTexto')}</p></R>
      </div>
    </>
  );
}
